/**
* Breadcrumbs — visible breadcrumb nav paired with the BreadcrumbList
* JSON-LD that every route already emits. Semantic <nav>/<ol> with
* aria-label + aria-current so screen readers and search bots agree
* on the hierarchy.
*/
import { Link, useRouterState } from "@tanstack/react-router";
import type { ComponentProps } from "react";
import { JsonLd } from "@/components/JsonLd";
import { absoluteUrl } from "@/config/sites";

export type BreadcrumbItem = {
  label: string;
  /** Absolute or root-relative href. Omit on the current page. */
  to?: string;
};

export function Breadcrumbs({
  items,
  className = "",
  jsonLd = false,
}: {
  items: BreadcrumbItem[];
  className?: string;
  /**
   * Emit the matching BreadcrumbList graph in-body. Opt-in, because most
   * routes already declare their own BreadcrumbList in `head()` and two
   * copies of the same list on one page is a validator conflict.
   */
  jsonLd?: boolean;
}) {
  // Google requires every ListItem to carry an `item` URL. The last crumb is
  // the current page and has no `to`, so resolve it from the live location —
  // this keeps every BreadcrumbList valid without touching each call site.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (!items.length) return null;
  return (
    <nav
      aria-label="Breadcrumb"
      className={`mx-auto max-w-6xl px-4 pt-4 text-sm text-muted-foreground ${className}`}
    >
      {jsonLd && (
        <JsonLd
          data={{
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: items.map((it, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: it.label,
              item: absoluteUrl(it.to ?? pathname),
            })),
          }}
        />
      )}
      <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1">

        {items.map((it, i) => {
          const last = i === items.length - 1;
          return (
            <li key={`${it.label}-${i}`} className="flex items-center gap-1.5">
              {i > 0 && <span aria-hidden="true" className="text-muted-foreground/50">/</span>}
              {last || !it.to ? (
                <span aria-current={last ? "page" : undefined} className="text-foreground/80">
                  {it.label}
                </span>
              ) : (
                <Link
                  to={it.to as ComponentProps<typeof Link>["to"]}
                  className="hover:text-foreground hover:underline"
                >
                  {it.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
